export interface ActiveSolutionItem {
  id?: string;
  name?: string;
}

interface SelectedSolutionLike {
  uniqueId?: string;
  displayName?: string;
}

/**
 * Decide the next `activeSolution` for the solutions-dropdown breadcrumb from the
 * XState `selectedSolution`. Returns `{ shouldUpdate: false }` when the current
 * value already matches (avoids a redundant setState), otherwise
 * `{ shouldUpdate: true, next }` with the value to apply (`null` on the home
 * page, where the breadcrumb shows "All Solutions").
 *
 * The "unchanged" guard compares BOTH the stable `uniqueId` AND the
 * `displayName` — not the id alone. A RENAME of the current solution keeps the
 * same `uniqueId` and only changes `displayName`, so an id-only guard left the
 * breadcrumb showing the stale name until a page reload (PSTUDIO-3596). Keeping
 * this pure lets it be unit-tested in the node env (pro-studio has no RTL).
 */
export function computeActiveSolution({
  selectedSolution,
  activeSolution,
  isHomePage,
}: {
  selectedSolution?: SelectedSolutionLike | null;
  activeSolution?: ActiveSolutionItem | null;
  isHomePage: boolean;
}): { shouldUpdate: boolean; next?: ActiveSolutionItem | null } {
  if (!selectedSolution) return { shouldUpdate: false };

  if (
    selectedSolution.uniqueId === activeSolution?.id &&
    selectedSolution.displayName === activeSolution?.name
  ) {
    return { shouldUpdate: false };
  }

  if (isHomePage) return { shouldUpdate: true, next: null };

  return {
    shouldUpdate: true,
    next: {
      id: selectedSolution.uniqueId,
      name: selectedSolution.displayName,
    },
  };
}

interface ScrollTopParams {
  /** Item top edge in viewport coordinates (getBoundingClientRect().top). */
  itemTop: number;
  /** Item bottom edge in viewport coordinates. */
  itemBottom: number;
  /** Container top edge in viewport coordinates. */
  containerTop: number;
  /** Container bottom edge in viewport coordinates. */
  containerBottom: number;
  /** Current container.scrollTop. */
  scrollTop: number;
}

/**
 * Pure scroll math for bringing a list item into view within its own
 * overflow container — the container is the only thing that moves, never the
 * page or the popover. Mirrors the design-system `scrollIntoView` vertical
 * behaviour but stays framework-free so it can be unit-tested in the node env
 * (pro-studio has no jsdom/RTL).
 *
 * Returns the new `scrollTop`:
 * - item above the viewport  → align its top edge to the container top
 * - item below the viewport  → align its bottom edge to the container bottom
 * - item already fully visible → unchanged
 */
export function computeScrollTopForItem({
  itemTop,
  itemBottom,
  containerTop,
  containerBottom,
  scrollTop,
}: ScrollTopParams): number {
  if (itemTop < containerTop) {
    return scrollTop + (itemTop - containerTop);
  }
  if (itemBottom > containerBottom) {
    return scrollTop + (itemBottom - containerBottom);
  }
  return scrollTop;
}

/**
 * The pages that survive a solution switch from the nav dropdown (PSTUDIO-4553),
 * as the URL tail after the solution root. A page is listed only when its URL
 * holds nothing but the solution: the channel pages carry a channelId that
 * belongs to the solution being left, so they land on Versions of the new one,
 * and so do Versions, Home and Configurations.
 *
 * Files is a layout with tabs (`/files/actions|media|assets`) and `/files`
 * itself only redirects to the first tab, so the tab segment is kept too. Its
 * pattern accepts any single segment instead of naming the three tabs, so a
 * tab added to the router later is kept without touching this list.
 */
const KEPT_PAGE_TAILS: readonly RegExp[] = [
  /^\/workflows$/,
  /^\/channels$/,
  /^\/default-messages$/,
  /^\/files(\/[^/]+)?$/,
];

function decodeAddress(pathname: string): string {
  try {
    return decodeURI(pathname);
  } catch {
    return pathname;
  }
}

/**
 * The URL tail to append to the new solution's root path so the user stays on
 * the page they were on, or '' to land on Versions. It is read from the address
 * the user is actually on, not from the route table: the table is keyed by names
 * that differ from the URL segments (`default_messages` lives at
 * `/default-messages`) and matches whole paths, so a page with a nested segment
 * such as a Files tab was never recognised. Answers '' while the solution root
 * is not known yet (no project or solution in the address).
 *
 * `pathname` is compared decoded: the router hands it over percent-encoded while
 * the root is built from decoded route params, so a solution name with a space
 * would otherwise never match and the user would land on Versions. An address
 * with a malformed escape is compared as it is, which lands on Versions.
 */
export function resolveKeptPageSuffix({
  pathname,
  solutionRoot,
}: {
  pathname: string;
  solutionRoot: string;
}): string {
  const address = decodeAddress(pathname);
  if (!solutionRoot || !address.startsWith(`${solutionRoot}/`)) return '';

  const tail = address.slice(solutionRoot.length).replace(/\/+$/, '');
  return KEPT_PAGE_TAILS.some((pattern) => pattern.test(tail)) ? tail : '';
}

export interface SolutionListItem {
  id?: string;
  name?: string;
}

interface RawSolutionLike {
  id?: string;
  displayName?: string | null;
}

/**
 * The dropdown's rows: `{ id, name }` per solution, sorted by name. Pure, so the
 * caller can memoise it on the context list (`useSolutionItems`) and hand the
 * dropdown the same array until a solution actually changes (PSTUDIO-4554).
 *
 * @todo move this sorting to the gql query; getSolutions has no sort argument yet.
 */
export function buildSolutionItems(
  solutions: readonly RawSolutionLike[] | undefined
): SolutionListItem[] {
  return (solutions ?? [])
    .map((bot) => ({ id: bot.id, name: bot.displayName ?? undefined }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/**
 * What to remember from the typeahead's `input` event: the ids of the rows that
 * matched, in the order it ranked them, or `null` for empty text (no filter, the
 * whole list). A match with no id cannot be looked up again, so it is dropped.
 */
export function filteredIdsFromTypeahead({
  filtered,
  value,
}: {
  filtered: readonly SolutionListItem[];
  value: string;
}): string[] | null {
  if (!value) return null;
  return filtered.flatMap((item) => (item.id ? [item.id] : []));
}

/**
 * The rows to draw for the typed filter. The typeahead reports which solutions
 * matched; only their ids are kept, and the rows are looked up in the CURRENT
 * list, so a rename shows while a filter is active and a deleted solution
 * disappears. `null` means no filter: the whole list, by identity.
 *
 * Limitation: the matches are decided by the typeahead on the keystroke, so a
 * solution added or renamed into a match while the text is unchanged does not
 * join the list until the next keystroke. The caller drops the filter when the
 * project changes, because the ids of one project's list match nothing in the
 * next.
 */
export function resolveFilteredOptions(
  items: readonly SolutionListItem[],
  filteredIds: readonly string[] | null
): SolutionListItem[] {
  if (filteredIds === null) return items as SolutionListItem[];
  const byId = new Map(items.map((item) => [item.id, item]));
  return filteredIds.flatMap((id) => {
    const item = byId.get(id);
    return item ? [item] : [];
  });
}
